import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import "./EditableTable.css"
import { TableRow, TableHeader } from './TableComponents';
import { useUnsavedChanges } from '../../../contexts/UnsavedChangesContext';
import useConfirmDialog from '@hooks/useConfirmDialog';
import { Spinner } from '../spinner/Spinner';
import { useLoading } from '@contexts/LoadingContext';
import { Button } from '../buttons/Button';
//memoized row for better performance - less re-renders
const MemoizedTableRow = React.memo(TableRow, (prevProps, nextProps) => {
  return (
    prevProps.rowData === nextProps.rowData &&
    prevProps.validationErrors === nextProps.validationErrors
  );
});

/**
 * EditableTable component - displays data with inputs that can be edited
 * 
 * @param {Object[]} data array of data from parent
 * @param {function} setData setter function that sets the data in the parent component
 * @param {Object} schema object that describes how the data should be displayed, input types, required fields, column titles, etc
 * @param {boolean} canDeleteRow boolean that determines if a delete button needs to be added to the row
 * @param {function} handleDeleteRow function that deletes the row from the data in parent component
 * @param {Object[]} validationErrors an array that contains the row number and column where errors occur: eg {row: 1, column: "Site_Name"}
 * 
 * @category Components - UI
 */
const EditableTable = ({ data, setData, schema, canDeleteRow = false, handleDeleteRow, handleToggleAPI, validationErrors = [], setValidationErrors = null, agmOptions = [], agmRawData = [], subchains = [], reorderChecked = null, jobIsLocked = false, isPaginated = false }) => {
  const { setHasUnsavedChanges } = useUnsavedChanges();
  const [ConfirmDialog, requestConfirm] = useConfirmDialog();
  const {hideLoading} = useLoading()

  //pagination
  const PAGE_SIZE = 200;
  const [page, setPage] = useState(0);

  const visibleData = useMemo(() => {
    if (!isPaginated) return data;
    const start = page * PAGE_SIZE;
    return data.slice(start, start + PAGE_SIZE);
  }, [data, page, PAGE_SIZE, isPaginated]);

  const [pageLoading, setPageLoading] = useState(true);
  
  useEffect(() => {
    setPageLoading(true);
    hideLoading()
  }, [page]); //eslint-disable-line

  useEffect(() => {
    if(data.length < PAGE_SIZE){
      setPage(0)
    }
  }, [data])

  useEffect(() => {
    if (!pageLoading) return;

    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setPageLoading(false);
      });
    });

    return () => {
      cancelAnimationFrame(raf1);
    };
  }, [pageLoading, visibleData]);

  /**
   * handleFieldChange: handles updating data in the parent component when an input is changed
   * 
   * @param {number} rowIndex index of the current row being changed
   * @param {*} columnId column currently being changed (key in row object), eg "Site_Name"
   * @param {Object} value change value, from input in table cell ex)e.target or {value: '', label: ''}
   * @param {function} setData setter function so data can be updated when table cell changed
   * 
   */
  const reorderCheckedRef = useRef(reorderChecked);

  useEffect(() => {
    reorderCheckedRef.current = reorderChecked;
  }, [reorderChecked])

  const handleFieldChange = useCallback((rowIndex, columnId, value, setData) => {
    setData(prevData => {
    const newValue = value?.value ?? value;
    const oldRow = prevData[rowIndex];
    const oldValue = oldRow[columnId];

    if (oldValue === newValue) return prevData;

    // If changing OrderNum, reorder following rows
    if (columnId === 'OrderNum' && !isNaN(newValue) && reorderCheckedRef.current) {
      
      const updatedData = [...prevData];

      // Set new OrderNum on current row
      updatedData[rowIndex] = { ...oldRow, OrderNum: parseInt(newValue) };

      let nextOrderNum = parseInt(newValue) + 1;

      // Recalculate OrderNum for all rows after current
      for (let i = rowIndex + 1; i < updatedData.length; i++) {
        updatedData[i] = {
          ...updatedData[i],
          OrderNum: nextOrderNum++
        };
      }

      return updatedData;
    }

    // Normal change for other fields
    const newRow = { ...oldRow, [columnId]: newValue };
    return prevData.map((row, idx) => (idx === rowIndex ? newRow : row));
  });

  if (
    columnId !== 'Dist_To_US' &&
    columnId !== 'Dist_To_DS' &&
    columnId !== 'Real_Chain_Delta'
  ) {
    setHasUnsavedChanges(true);
  }
}, [setData]); //eslint-disable-line

  const memoizedSchema = useMemo(() => schema, [schema]);
  const memoizedValidationErrors = useMemo(() => validationErrors, [validationErrors]);
  const memoizedAgmRawData = useMemo(() => agmRawData, [agmRawData]);
  const memoizedSubchains = useMemo(() => subchains, [subchains]);

  // main table component
  return (
    <div>
      {/* show spinner when page loading */}
      <div className='h-4 flex justify-center'>
        { pageLoading && <Spinner className='text-2xl mt-4'/>}

      </div>

      <div className='flex flex-col martrak-table-container martrak-scroll-container border my-6'>

        <table className='container table mx-auto justify-center items-center rounded-lg martrak-table border-collapse'>

          {/* header */}
          <TableHeader
            schema={schema}
            canDeleteRow={canDeleteRow}
          />

          {/* body */}
          <tbody>
            {visibleData.map((rowData, rowIndex) => {
              const absoluteRowIndex = isPaginated
                ? page * PAGE_SIZE + rowIndex
                : rowIndex;

              return(

              <MemoizedTableRow
                key={`table-row-${absoluteRowIndex}`}
                rowData={rowData}
                rowIndex={absoluteRowIndex}
                schema={memoizedSchema}
                handleFieldChange={handleFieldChange}
                setData={setData}
                canDeleteRow={canDeleteRow}
                handleDeleteRow={handleDeleteRow}
                handleToggleAPI={handleToggleAPI}
                validationErrors={memoizedValidationErrors}
                agmOptions={agmOptions}
                agmRawData={memoizedAgmRawData}
                requestConfirm={requestConfirm}
                subchains={memoizedSubchains}
                jobIsLocked={jobIsLocked}
              />
              )

            })}

          </tbody>
        </table>
      </div>

      {/* pages with prev/next buttons */}
      {isPaginated && (
        <div className="flex gap-2 my-2 justify-center">
          <Button
            disabled={page === 0}
            onClick={() => setPage(p => p - 1)}
            variant='noBackground'

          >
            Prev
          </Button>

          {/* current page */}
          <div className='flex flex-col items-center'>

            <span>
              Page {page + 1} of {Math.ceil(data.length / PAGE_SIZE)}
            </span>

            <span className="text-sm text-gray-500">
              {page * PAGE_SIZE + 1}–
              {Math.min((page + 1) * PAGE_SIZE, data.length)} of {data.length}
            </span>
          </div>

          <Button
            disabled={(page + 1) * PAGE_SIZE >= data.length}
            onClick={() => setPage(p => p + 1)}
            variant='noBackground'
          >
            Next
          </Button>
        </div>
      )}

      <ConfirmDialog />
    </div>
  );
};

export default EditableTable;